import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Typography,
  Stack,
  Switch,
  Input,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  toast,
} from "@altimateai/lego";
import { IconPlus } from "@tabler/icons-react";
import {
  useSSOTeamSyncConfig,
  useUpdateSSOTeamSyncConfig,
  useSSOGroupMappings,
  useDeleteSSOGroupMapping,
  useSSOTargets,
  useConfigForm,
} from "./hooks";
import { MappingTable } from "./MappingTable";
import { AddMappingDialog } from "./AddMappingDialog";
import { BulkImportCard } from "./BulkImportCard";
import { ProviderFallbackConfig } from "./ProviderFallbackConfig";
import type { SSOGroupMappingResponse } from "./types";

export const SSOGroupSyncSettings = () => {
  const [activeTab, setActiveTab] = useState("team");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMappingType, setDialogMappingType] = useState<"team" | "role">("team");
  const [editMapping, setEditMapping] = useState<SSOGroupMappingResponse | null>(null);

  // Config
  const { data: config, isLoading: configLoading } = useSSOTeamSyncConfig();
  const updateConfig = useUpdateSSOTeamSyncConfig();

  // Mappings
  const { data: teamMappings, isLoading: teamLoading } = useSSOGroupMappings("team");
  const { data: roleMappings, isLoading: roleLoading } = useSSOGroupMappings("role");
  const deleteMutation = useDeleteSSOGroupMapping();

  // Fetch teams and roles from /targets endpoint
  const { data: targets } = useSSOTargets();
  const teams = targets?.teams ?? [];
  const roles = targets?.roles ?? [];

  const handleToggleEnabled = async () => {
    const newEnabled = !config?.enabled;
    try {
      await updateConfig.mutateAsync({ enabled: newEnabled });
      toast({
        title: newEnabled ? "SSO Group Sync enabled" : "SSO Group Sync disabled",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Failed to update config",
        description: String(error),
      });
    }
  };

  // Local config form state — only saved when user clicks Save
  const configForm = useConfigForm(config);

  const handleConfigSave = async () => {
    try {
      await updateConfig.mutateAsync(configForm.getPayload());
      configForm.markClean();
      toast({ title: "Configuration saved" });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Failed to save config",
        description: String(error),
      });
    }
  };

  const handleAddMapping = (type: "team" | "role") => {
    setDialogMappingType(type);
    setEditMapping(null);
    setDialogOpen(true);
  };

  const handleEditMapping = (mapping: SSOGroupMappingResponse) => {
    setDialogMappingType(mapping.mapping_type === "role" ? "role" : "team");
    setEditMapping(mapping);
    setDialogOpen(true);
  };

  const handleDeleteMapping = async (mappingId: number) => {
    try {
      await deleteMutation.mutateAsync(mappingId);
      toast({ title: "Mapping deleted" });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Failed to delete mapping",
        description: String(error),
      });
    }
  };

  return (
    <div className="al-p-6 al-max-w-5xl al-mx-auto al-space-y-6">
      <Typography variant="page-heading">SSO Group Sync</Typography>
      <Typography variant="body" className="al-text-gray-500">
        Automatically assign users to teams and roles based on their SSO group claims.
      </Typography>

      <Card>
        <CardHeader>
          <Stack direction="row" className="al-justify-between al-items-center">
            <CardTitle>Configuration</CardTitle>
            <Stack direction="row" className="al-items-center al-gap-2">
              <Typography variant="body" className="al-text-sm">
                {config?.enabled ? "Enabled" : "Disabled"}
              </Typography>
              <Switch
                checked={config?.enabled ?? false}
                onCheckedChange={handleToggleEnabled}
                disabled={configLoading || updateConfig.isPending}
                aria-label="Enable SSO Group Sync"
              />
            </Stack>
          </Stack>
        </CardHeader>
        {config?.enabled && (
          <CardContent className="al-space-y-6">
            <div className="al-grid al-grid-cols-2 al-gap-4">
              <div>
                <Typography variant="body" className="al-text-sm al-font-medium al-mb-1">
                  Group Claim Name
                </Typography>
                <Input
                  value={configForm.form.group_claim_name}
                  onChange={e => configForm.update("group_claim_name", e.target.value)}
                  placeholder="groups"
                />
                <Typography variant="body" className="al-text-xs al-text-muted-foreground al-mt-1">
                  The OIDC/SAML claim containing group memberships. Both Azure AD and Okta use
                  &quot;groups&quot; by default.
                </Typography>
              </div>
              <div>
                <Typography variant="body" className="al-text-sm al-font-medium al-mb-1">
                  Group Prefix Filter
                </Typography>
                <Input
                  value={configForm.form.group_prefix_filter}
                  onChange={e => configForm.update("group_prefix_filter", e.target.value)}
                  placeholder="Optional prefix filter"
                />
                <Typography variant="body" className="al-text-xs al-text-muted-foreground al-mt-1">
                  Only process groups whose names start with this prefix. Leave empty to process all
                  groups.
                </Typography>
              </div>
            </div>

            <div className="al-border-t al-pt-4">
              <Typography variant="body" className="al-text-sm al-font-semibold al-mb-1">
                Identity Provider
              </Typography>
              <Typography variant="body" className="al-text-xs al-text-muted-foreground al-mb-3">
                Select your SSO provider for relevant setup guidance. If your provider limits the
                number of groups in the token, you can enable direct API access to ensure all group
                memberships are captured.
              </Typography>
              <ProviderFallbackConfig
                configForm={configForm.form}
                onUpdate={configForm.update}
                disabled={updateConfig.isPending}
              />
            </div>

            <Stack direction="row" className="al-justify-end al-pt-2">
              <Button
                size="sm"
                onClick={handleConfigSave}
                disabled={!configForm.dirty || updateConfig.isPending}
              >
                {updateConfig.isPending ? "Saving..." : "Save Configuration"}
              </Button>
            </Stack>
          </CardContent>
        )}
      </Card>

      {config?.enabled && (
        <>
          <BulkImportCard />

          <Card>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <CardHeader>
                <CardTitle>Group Mappings</CardTitle>
                <Stack direction="row" className="al-justify-between al-items-center al-mt-2">
                  <TabsList>
                    <TabsTrigger value="team">Team Mappings</TabsTrigger>
                    <TabsTrigger value="role">Role Mappings</TabsTrigger>
                  </TabsList>
                  <Button size="sm" onClick={() => handleAddMapping(activeTab as "team" | "role")}>
                    <IconPlus size={16} className="al-mr-1" />
                    Add Mapping
                  </Button>
                </Stack>
              </CardHeader>
              <CardContent>
                <TabsContent value="team" className="al-mt-0">
                  <MappingTable
                    mappings={teamMappings ?? []}
                    mappingType="team"
                    isLoading={teamLoading}
                    onEdit={handleEditMapping}
                    onDelete={handleDeleteMapping}
                    isDeleting={deleteMutation.isPending}
                  />
                </TabsContent>
                <TabsContent value="role" className="al-mt-0">
                  <MappingTable
                    mappings={roleMappings ?? []}
                    mappingType="role"
                    isLoading={roleLoading}
                    onEdit={handleEditMapping}
                    onDelete={handleDeleteMapping}
                    isDeleting={deleteMutation.isPending}
                  />
                </TabsContent>
              </CardContent>
            </Tabs>
          </Card>

          {/* Add/Edit Dialog */}
          <AddMappingDialog
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            mappingType={dialogMappingType}
            editMapping={editMapping}
            teams={teams}
            roles={roles}
          />
        </>
      )}
    </div>
  );
};
